import _ from 'lodash';
import { authAxios } from './AuthServiceHelper';
import {
  DL_EP_INPUT_DISABLE_FILTER_ID,
  LV_DEPTH_EP_CATALOG,
  LeistungsSlices,
  tags as tagTitleMap,
  URL_TO_LVEP_SERVER
} from '../Statics/Constants';
import { ErrorResponseType } from './LoginNetworkHelper';
import { errorMsgToString } from './ErrorMessages';
import { UUID } from 'crypto';
import { TreeTableExpandedKeysType } from 'primereact/treetable';

export type MinimizedEpCatalog = {
  id: string;
  phaseId: string;
  createdAt?: string;
  filename?: string;
  /** From {@link CopyCatalogInfo} when meta originates from external copy. */
  catalogName?: string;
  versionName?: string;
  description?: string;
};

export type MinimizedLvCatalog = {
  id: string;
  phaseId: string;
  propertyId: string;
  createdAt?: string;
  filename?: string;
};

export type EnlargedLvPosition = {
  id: string;
  serialNumber?: string;
  identificationNumber?: string;
  amount?: number;
  description?: string;
  optionalPosition: boolean;
  optionalPositionActive: boolean;
  epCode: string;
  //connections to EP
  leftInt: number;
  rightInt: number;
  unit?: string;
  sliceTag?: string;
  epPriceId: string;
  blFactorId: string;
  lsFactorId: string;
  nFactorId: string;
  mFactorId: string;
};

export type ConnectedLvPosition = {
  id: string;
  amount?: number;
  propertyId?: string;
};

export type EpPosition = {
  id: string;
  leftInt: number;
  rightInt: number;
  number?: string;
  title?: string;
  shortTitle?: string;
  epCode?: string;
  tags?: string[];
  unit?: string;
  preText?: string;
  postText?: string;
  sliceTag?: string;
  hidden: boolean;
  connectedLvPositions?: ConnectedLvPosition[];
};

export type EP = {
  info: MinimizedEpCatalog;
  data: EpPosition[];
};

/** Request body for PATCH `/v1/phase/{phaseId}/epPosition`. */
export type PatchEpPositionsTagsRequest = {
  /**
   * Map of EP position ID → tag ID.
   * Example: { values: { "posId1": "tagA", "posId2": "tagB" } }
   */
  values: Record<string, string>;
};

export type EpTreeNodeData = {
  leftInt: number;
  rightInt: number;
  title?: string;
  shortTitle?: string;
  type?: 'position' | 'category';
  number?: string;
  hidden?: boolean;
  epCode?: string;
  unit?: string;
  postText?: string;
  tags?: string[];
  tagIcons?: string[];
  tagTitles?: string[];
};

/** Nested EP catalog node (tree views, tagging, etc.). */
export type EpTreeNode = {
  key: string;
  depth: number;
  className?: string;
  data: EpTreeNodeData;
  children?: EpTreeNode[] | null;
};
const tagIconLookup: Record<string, string> = {};
LeistungsSlices.forEach((slice) => {
  slice.tag.forEach((tagKey) => {
    tagIconLookup[tagKey] = slice.icon;
  });
});

function resolveTagIcons(tags: string[]): string[] {
  const icons = new Set<string>();
  tags.forEach((tag) => {
    const icon = tagIconLookup[tag];
    if (icon) {
      icons.add(icon);
    }
  });
  return Array.from(icons);
}

function resolveTagTitles(tags: string[]): string[] {
  const titles = new Set<string>();
  tags.forEach((tag) => {
    const title = tagTitleMap[tag];
    if (title) {
      titles.add(title);
    }
  });
  return Array.from(titles);
}
export type CatalogProject = {
  id: UUID;
  organisationId: UUID; // organisation_id CHAR(36)
  createdAt: string; // TIMESTAMP
  updatedAt: string; // TIMESTAMP
  title: string; // TEXT
  description?: string | null; // TEXT nullable
  publicAccess: boolean; // TINYINT -> boolean
  catalogVersions: CatalogVersion[];
};
export type CatalogVersion = {
  id: UUID;
  catalogProjectId: UUID; // catalogProject_id CHAR(36)
  versionTitle: string; // TEXT
  createdAt: string; // TIMESTAMP
  deletedAt?: string | null; // TIMESTAMP nullable
  updatedAt: string; // TIMESTAMP
  isCompleted?: boolean;
  catalogCategories?: any[]; // Optional tree data for versions - using any[] to avoid circular dependency with DTO types
  // Optional counts provided by the backend DTO
  positionCount?: number;
  categoryCount?: number;
};
export type CopyCatalogEP = {
  info: CopyCatalogInfo;
  data: EpPosition[];
};

export type CopyCatalogInfo = {
  id: UUID;
  phaseId: UUID;
  createdAt: string;
  filename: string;
  catalogName: string;
  versionName: string;
  lastVersionUpdate: string;
  description: string;
  amountCategories: number;
  amountPositions: number;
};

/**
 * This method reads the meta information of EP-Catalog for all phases
 */
export const readEpCatalogMetaForAllPhases = () => {
  return new Promise((resolve, reject) => {
    authAxios({
      method: 'get',
      url: URL_TO_LVEP_SERVER + '/v1/epCatalog'
    })
      .then((result: any) => {
        let answer: MinimizedEpCatalog[] = result.data.data;
        resolve(answer);
      })
      .catch((error: ErrorResponseType) => {
        if (error && error.response) {
          reject(error.response);
        } else {
          reject(error);
        }
      });
  });
};

/**
 * This method is for downloading the EP Catalog for a given phase as excel
 * @param phaseId
 * @returns
 */
export const readEpCatalogAsExcelForProperty = (phaseId: String) => {
  return new Promise((resolve, reject) => {
    const src =
      //URL_TO_LVEP_SERVER + '/v1/phase/' + phaseId + '/epCatalog/excel';//
      'http://localhost:13018/v1/phase/' + phaseId + '/epCatalog/excel';

    authAxios({
      method: 'get',
      url: src,
      responseType: 'blob'
    })
      .then((response) => {
        const entry = response.data;
        resolve(entry);
      })
      .catch((error) => {
        if (error && error.response) {
          reject(error.response);
        } else {
          reject(error);
        }
      });
  });
};

/**
 * This method uploads the given excel to create a new EP Catalog for the given Phase
 * @param phaseId
 * @param file
 * @returns
 */
export const createNewEpCatalogForPropertyFromExcel = (
  phaseId: String,
  file: any
) => {
  return new Promise((resolve, reject) => {
    const src =
      URL_TO_LVEP_SERVER + '/v1/phase/' + phaseId + '/epCatalog/excel';
    const fd = new FormData();
    fd.append('file', file);
    authAxios
      .put(src, fd)
      .then((res) => {
        const entry: EP = res.data;
        resolve(entry);
      })
      .catch((error) => {
        if (error && error.response) {
          reject(error.response);
        } else {
          reject(error);
        }
      });
  });
};

/**
 * This method replaces the current ep Catalog with the given excel for the given Phase
 * @param phaseId
 * @param file
 * @returns
 */
export const replaceEpCatalogForPhaseFromExcel = (
  phaseId: String,
  file: any
) => {
  return new Promise((resolve, reject) => {
    const src =
      URL_TO_LVEP_SERVER + '/v1/phase/' + phaseId + '/epCatalog/excel';
    const fd = new FormData();
    fd.append('file', file);
    authAxios
      .patch(src, fd)
      .then((res) => {
        const entry: EP = res.data;
        resolve(entry);
      })
      .catch((error) => {
        reject(errorMsgToString(error));
      });
  });
};

/**
 * This method reads the whole EP for the given phase
 * @param phaseId
 * @returns
 */
export const readEpCatalogForPhase = (phaseId: String) => {
  return new Promise((resolve, reject) => {
    authAxios({
      method: 'get',
      url: URL_TO_LVEP_SERVER + '/v1/phase/' + phaseId + '/epCatalog'
    })
      .then((result: any) => {
        let answer: EP = result.data;
        resolve(answer);
      })
      .catch((error: ErrorResponseType) => {
        if (error && error.response) {
          reject(error.response);
        } else {
          reject(error);
        }
      });
  });
};

/**
 * Updates EP position tags (Leistungscluster) for a phase.
 * Response: full EP catalog {@link EP}.
 */
export const patchEpPositionTagsForPhase = (
  phaseId: string,
  body: PatchEpPositionsTagsRequest
) => {
  return new Promise<EP>((resolve, reject) => {
    authAxios({
      method: 'patch',
      url: URL_TO_LVEP_SERVER + '/v1/phase/' + phaseId + '/epPosition',
      data: body
    })
      .then((result: { data: EP }) => {
        resolve(result?.data);
      })
      .catch((error: ErrorResponseType) => {
        if (error && error.response) {
          reject(error.response);
        } else {
          reject(error);
        }
      });
  });
};

export const deleteEpCatalogForPhase = (phaseId: String, catalogId: String) => {
  return new Promise((resolve, reject) => {
    authAxios({
      method: 'delete',
      url:
        URL_TO_LVEP_SERVER + '/v1/phase/' + phaseId + '/epCatalog/' + catalogId
    })
      .then(() => {
        resolve('Success');
      })
      .catch((error: ErrorResponseType) => {
        if (error && error.response) {
          reject(error.response);
        } else {
          reject(error);
        }
      });
  });
};

///v1/epCatalog/external
export const readAllEpCatalogsFromPlatform = () => {
  return new Promise<CatalogProject[]>((resolve, reject) => {
    authAxios({
      method: 'get',
      url: URL_TO_LVEP_SERVER + '/v1/external/epCatalog'
    })
      .then((result) => {
        let answer: CatalogProject[] = result?.data?.catalogProjects ?? [];
        resolve(answer);
      })
      .catch((error: ErrorResponseType) => {
        if (error && error.response) {
          reject(error?.response);
        } else {
          reject(error);
        }
      });
  });
};

/**
 * Copies an external EP catalog version (platform library) onto the given phase.
 * Response shape: {@link CopyCatalogEP}.
 */
export const copyExternalEpCatalogVersionToPhase = (
  phaseId: string,
  epCatalogProjectId: string,
  versionId: string
) => {
  return new Promise<CopyCatalogEP>((resolve, reject) => {
    const url =
      URL_TO_LVEP_SERVER +
      '/v1/phase/' +
      phaseId +
      '/external/epCatalog/' +
      epCatalogProjectId +
      '/version/' +
      versionId +
      '/copy';
    authAxios({
      method: 'put',
      url
    })
      .then((result: { data: CopyCatalogEP }) => {
        resolve(result?.data);
      })
      .catch((error: ErrorResponseType) => {
        if (error && error.response) {
          reject(error.response);
        } else {
          reject(error);
        }
      });
  });
};

export const generateEpTreeFromPositions = (
  positions: EpPosition[] | null | undefined
): EpTreeNode[] | null => {
  if (!positions?.length) {
    return null;
  }
  //search boundaries
  let smallestLeftInt = 1000000;
  let largestRightInt = 0;
  positions.forEach((entry) => {
    if (entry.leftInt < smallestLeftInt) {
      smallestLeftInt = entry.leftInt;
    }
    if (entry.rightInt > largestRightInt) {
      largestRightInt = entry.rightInt;
    }
  });
  //create surrounding node
  const result: EpTreeNode = {
    key: 'delete',
    depth: 0,
    data: {
      leftInt: smallestLeftInt - 1,
      rightInt: largestRightInt + 1
    },
    children: null
  };
  //sort values
  const data: EpPosition[] = _.sortBy(positions, [
    function (entry: EpPosition) {
      return entry.leftInt;
    }
  ]);
  for (let i = 0; i < data.length; i++) {
    const entry: EpPosition = data[i];
    const parent = searchTreePosition(result, entry.leftInt, entry.rightInt);
    if (!parent) {
      continue;
    }
    const tags: string[] = extractTags(entry);
    parent.children = parent.children ? parent.children : [];
    parent.children.push({
      key: entry.id,
      depth: parent.depth + 1,
      className: entry.epCode ? 'p' : 'c-' + (parent.depth + 1),
      data: {
        title: entry.title,
        shortTitle: entry.shortTitle,
        type: entry.epCode ? 'position' : 'category',
        number: entry.number,
        hidden: entry.hidden,
        epCode: entry.epCode,
        unit: entry.unit,
        leftInt: entry.leftInt,
        rightInt: entry.rightInt,
        postText: entry.postText,
        tags: tags,
        tagIcons: resolveTagIcons(tags),
        tagTitles: resolveTagTitles(tags)
      }
    });
  }
  return result.children ?? null;
};

/** Tree nodes from {@link generateEpTreeFromPositions} — walk and collect leaf rows for tagging UIs. */
export type FlattenedEpTreePosition = {
  id: string;
  epCode?: string;
  title?: string;
};

export function flattenEpTreePositions(
  nodes: EpTreeNode[] | null | undefined
): FlattenedEpTreePosition[] {
  const result: FlattenedEpTreePosition[] = [];
  function walk(arr: EpTreeNode[] | undefined | null) {
    if (!arr) return;
    arr.forEach((n) => {
      if (!n) return;
      if (n.data?.type === 'position') {
        result.push({
          id: n.key,
          epCode: n.data?.epCode,
          title: n.data?.title ?? n.data?.shortTitle
        });
      }
      if (n.children && n.children.length > 0) {
        walk(n.children);
      }
    });
  }
  walk(nodes ?? []);
  return result;
}

export function findEpTreeNodeByKey(
  nodes: EpTreeNode[] | null | undefined,
  key: string
): EpTreeNode | null {
  if (!nodes) {
    return null;
  }
  for (const node of nodes) {
    if (node.key === key) {
      return node;
    }
    const child = findEpTreeNodeByKey(node.children ?? null, key);
    if (child) {
      return child;
    }
  }
  return null;
}

function collectPositionIdsInSubtree(node: EpTreeNode, acc: string[]) {
  if (node.data.type === 'position') {
    acc.push(node.key);
  }
  if (node.children && node.children.length > 0) {
    node.children.forEach((child) => collectPositionIdsInSubtree(child, acc));
  }
}

export function collectDescendantPositionIds(
  nodes: EpTreeNode[] | null | undefined,
  key: string
): string[] {
  const result: string[] = [];
  const root = findEpTreeNodeByKey(nodes, key);
  if (!root) {
    return result;
  }
  collectPositionIdsInSubtree(root, result);
  return result;
}

function epNodeSearchHaystack(data: EpTreeNodeData): string {
  return [
    data.title,
    data.shortTitle,
    data.number,
    data.epCode,
    data.unit,
    data.postText
  ]
    .filter((p) => p != null && String(p).trim() !== '')
    .join(' ')
    .toLowerCase();
}

function nodeMatchesQuery(node: EpTreeNode, q: string): boolean {
  if (!q) return true;
  return epNodeSearchHaystack(node.data).includes(q);
}

function cloneEpSubtree(nodes: EpTreeNode[]): EpTreeNode[] {
  return nodes.map((n) => ({
    ...n,
    data: { ...n.data },
    children: n.children?.length ? cloneEpSubtree(n.children) : undefined
  }));
}

function filterEpNodeByQuery(node: EpTreeNode, q: string): EpTreeNode | null {
  const children = node.children ?? [];
  const filteredKids = children
    .map((child) => filterEpNodeByQuery(child, q))
    .filter((child): child is EpTreeNode => child != null);

  if (node.data.type === 'position') {
    if (q && !nodeMatchesQuery(node, q)) {
      return null;
    }
    return { ...node, data: { ...node.data }, children: undefined };
  }

  if (!q || nodeMatchesQuery(node, q)) {
    return {
      ...node,
      data: { ...node.data },
      children: children.length ? cloneEpSubtree(children) : undefined
    };
  }

  if (filteredKids.length) {
    return {
      ...node,
      data: { ...node.data },
      children: filteredKids
    };
  }

  return null;
}

export function filterEpTreeForSearch(
  nodes: EpTreeNode[] | null | undefined,
  query: string
): EpTreeNode[] {
  if (!nodes?.length) {
    return [];
  }
  const q = query.trim().toLowerCase();
  if (!q) {
    return nodes;
  }
  const result: EpTreeNode[] = [];
  for (const node of nodes) {
    const filtered = filterEpNodeByQuery(node, q);
    if (filtered) {
      result.push(filtered);
    }
  }
  return result;
}

function filterEpNodeByPredicate(
  node: EpTreeNode,
  predicate: (node: EpTreeNode) => boolean
): EpTreeNode | null {
  if (node.data.type === 'position') {
    if (!predicate(node)) {
      return null;
    }
    return { ...node, data: { ...node.data }, children: undefined };
  }
  const filteredKids = (node.children ?? [])
    .map((child) => filterEpNodeByPredicate(child, predicate))
    .filter((child): child is EpTreeNode => child != null);

  if (!filteredKids.length) {
    return null;
  }

  return {
    ...node,
    data: { ...node.data },
    children: filteredKids
  };
}

export function filterEpTreeForTagging(
  nodes: EpTreeNode[] | null | undefined,
  query: string,
  positionPredicate?: (node: EpTreeNode) => boolean
): EpTreeNode[] {
  const searched = filterEpTreeForSearch(nodes, query);
  if (!positionPredicate) {
    return searched;
  }
  const result: EpTreeNode[] = [];
  for (const node of searched) {
    const filtered = filterEpNodeByPredicate(node, positionPredicate);
    if (filtered) {
      result.push(filtered);
    }
  }
  return result;
}

export function collectExpandedKeys(
  nodes: EpTreeNode[],
  maxDepth?: number
): TreeTableExpandedKeysType {
  const keys: TreeTableExpandedKeysType = {};
  function walk(list: EpTreeNode[]) {
    for (const n of list) {
      if (maxDepth == null || n.depth <= maxDepth) {
        keys[n.key] = true;
      }
      if (n.children?.length) {
        walk(n.children);
      }
    }
  }
  walk(nodes);
  return keys;
}

export type EpTaggingIndex = {
  positionAncestors: Record<string, string[]>;
  initialHasTag: Record<string, boolean>;
  categoryTotalLeaves: Record<string, number>;
  totalLeaves: number;
  totalTaggedLeaves: number;
};

export function buildEpTaggingIndex(
  nodes: EpTreeNode[] | null | undefined,
  tags: string[]
): EpTaggingIndex {
  const positionAncestors: Record<string, string[]> = {};
  const initialHasTag: Record<string, boolean> = {};
  const categoryTotalLeaves: Record<string, number> = {};
  let totalLeaves = 0;
  let totalTaggedLeaves = 0;
  if (!nodes?.length) {
    return {
      positionAncestors,
      initialHasTag,
      categoryTotalLeaves,
      totalLeaves,
      totalTaggedLeaves
    };
  }
  const tagSet = new Set(tags ?? []);
  function walk(list: EpTreeNode[], ancestors: string[]) {
    for (const node of list) {
      if (!node) {
        continue;
      }
      if (node.data?.type === 'position') {
        const positionId = node.key;
        positionAncestors[positionId] = [...ancestors];
        totalLeaves += 1;
        const hasTag =
          (node.data?.tags ?? []).find((t) => tagSet.has(t)) != null;
        initialHasTag[positionId] = hasTag;
        if (hasTag) {
          totalTaggedLeaves += 1;
        }
        ancestors.forEach((categoryId) => {
          categoryTotalLeaves[categoryId] =
            (categoryTotalLeaves[categoryId] ?? 0) + 1;
        });
        continue;
      }
      if (node.children?.length) {
        walk(node.children, [...ancestors, node.key]);
      }
    }
  }
  walk(nodes, []);
  return {
    positionAncestors,
    initialHasTag,
    categoryTotalLeaves,
    totalLeaves,
    totalTaggedLeaves
  };
}

function extractTags(entry: EpPosition) {
  let result = [];
  let added: boolean = false;
  if (entry && entry.sliceTag) {
    if (entry.sliceTag) {
      result.push(entry.sliceTag);
    }
    if (entry.connectedLvPositions && entry.connectedLvPositions.length > 0) {
      entry.connectedLvPositions.forEach((e: ConnectedLvPosition) => {
        if (e.propertyId) {
          added = true;
          result.push(e.propertyId);
        }
      });
    }
  }
  //if no prop id was added
  if (!added) {
    result.push(DL_EP_INPUT_DISABLE_FILTER_ID);
  }
  return result;
}

export function removeAllHiddenCategoriesFromEpTree(
  data: EpTreeNode[] | null | undefined,
  lvMode: boolean
): EpTreeNode[] | null {
  if (data) {
    const result: EpTreeNode[] = [];
    for (let x = 0; x < data.length; x++) {
      const node = data[x];
      const adapted = clearHiddenCategoriesInNode(node, lvMode);
      if (adapted) {
        result.push(adapted);
      }
    }
    return result;
  }
  return null;
}

function clearHiddenCategoriesInNode(
  node: EpTreeNode,
  lvMode: boolean
): EpTreeNode | null {
  if (node) {
    //remove all hidden categories by copy all children to it`s parent
    const result: EpTreeNode = _.cloneDeep(node);
    let parent = null;
    do {
      parent = searchParentCategoryWithHiddenChild(result, lvMode);
      if (parent) {
        //copy children to parent's children
        const newChildren: EpTreeNode[] = [];
        const siblings = parent.children ?? [];
        for (let x = 0; x < siblings.length; x++) {
          const c = siblings[x];
          if (
            c.data.type === 'category' &&
            (c.data.hidden === true ||
              (lvMode &&
                LV_DEPTH_EP_CATALOG !== -1 &&
                c.depth > LV_DEPTH_EP_CATALOG))
          ) {
            //if child has children to copy
            if (c.children && c.children.length > 0) {
              for (let a = 0; a < c.children.length; a++) {
                newChildren.push(c.children[a]);
              }
            }
          } else {
            newChildren.push(c);
          }
        }
        parent.children = newChildren;
      }
    } while (parent !== null);
    return result;
  }
  return null;
}

function searchParentCategoryWithHiddenChild(
  root: EpTreeNode,
  lvMode: boolean
): EpTreeNode | null {
  if (root.children && testArrayIfCatIsHidden(root.children, lvMode)) {
    return root;
  } else if (root.children != null) {
    let i;
    let result = null;
    for (i = 0; result == null && i < root.children.length; i++) {
      result = searchParentCategoryWithHiddenChild(root.children[i], lvMode);
    }
    return result;
  }
  return null;
}

function testArrayIfCatIsHidden(data: EpTreeNode[], lvMode: boolean) {
  for (let i = 0; i < data.length; i++) {
    const element = data[i];
    if (element && element.data) {
      if (element.data.type === 'category') {
        if (element.data.hidden === true) {
          return true;
        }
        //lv cleaning
        if (lvMode && LV_DEPTH_EP_CATALOG !== -1) {
          if (element.depth > LV_DEPTH_EP_CATALOG) {
            return true;
          }
        }
      }
    }
  }
  return false;
}

export function searchTreePosition(
  root: EpTreeNode,
  leftInt: number,
  rightInt: number
): EpTreeNode | null {
  if (root.data.leftInt < leftInt && root.data.rightInt > rightInt) {
    if (root.children) {
      let pay = null;
      for (let x = 0; pay == null && x < root.children.length; x++) {
        pay = searchTreePosition(root.children[x], leftInt, rightInt);
      }
      if (pay) {
        return pay;
      } else {
        return root;
      }
    } else {
      return root;
    }
  } else {
    return null;
  }
}
